import React, { useState } from 'react';
import {
	Box,
	Typography,
	IconButton,
	Modal,
	Button,
	Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { DARK_MODE, PRINTER_ERROR, PRINTER_LOADING, ZPL_TEMPLATE_FOR_BARCODE } from '../../constants';
import { axiosInstance } from '../../api/api';
import { useMessageContextManager } from '../MessageOverlay/MessageOverlay';
import { useResolutionScale } from '../../hooks/useResolutionScale';
import { BasicTable, LargeButton, ScanOrEnterBarcodeForm } from '..';
import { formatZplTemplate } from '../../utils/utils';

const DEFAULT_COLUMNS = [
	{
		width: 250,
		title: 'Serial Number (VIN)',
		field: 'serialNumber',
		align: 'center',
	},
];

const UpdateSerialNo = ({ selectedRows, handleClose }) => {
	const handleSubmit = () => {
		if (selectedRows && selectedRows.length > 0) {
			const activeRow = selectedRows[0];
			let zplString = '';

			// Check if backend provided a ZPL label
			if (activeRow.labels && activeRow.labels.length > 0 && activeRow.labels[0].value) {
				zplString = activeRow.labels[0].value;
			} else {
				// Fallback: Create the ZPL properly using the template
				zplString = formatZplTemplate(ZPL_TEMPLATE_FOR_BARCODE, activeRow.partNumber || activeRow.serialNumber || '');
			}

			const zplEncodedString = encodeURIComponent(zplString);
			window.location.href = `nirmaan://?cmd=print&zplstring=${zplEncodedString}`;

			setTimeout(() => {
				handleClose();
			}, 3000);
		}
	};

	return (
		<Grid
			container
			paddingX={3}
			paddingTop={2}
			paddingBottom={2}
			alignItems="center"
			gap={2}
			justifyContent="center"
		>
			<LargeButton
				backgroundColor="#0F107B"
				name="CANCEL"
				handleButtonClick={handleClose}
				test-id="CANCEL"
			/>
			<LargeButton
				backgroundColor="#0F107B"
				name="PRINT BARCODE"
				handleButtonClick={handleSubmit}
				disabled={selectedRows.length !== 1}
				test-id="SUBMIT"
			/>
		</Grid>
	);
};

const ReprintBarcodeModal = ({ handleClose }) => {
	const themeMode = useSelector((state) => state?.theme?.themeMode);
	const isDark = themeMode === DARK_MODE;
	const { scaleValue } = useResolutionScale();
	const { showMessageOverlay } = useMessageContextManager();

	const [resetTrigger, setResetTrigger] = useState(0);

	const onSubmit = (value, resetBarcodeValue) => {
		// UI-side implementation only: Directly print the scanned value
		try {
			// Create the ZPL properly using the template
			const zplString = formatZplTemplate(ZPL_TEMPLATE_FOR_BARCODE, value || '');

			const zplEncodedString = encodeURIComponent(zplString);
			window.location.href = `nirmaan://?cmd=print&zplstring=${zplEncodedString}`;

			showMessageOverlay({
				contextText: 'Printing Barcode...',
				success: true,
			});

			setTimeout(() => {
				handleClose();
			}, 2000);
		} catch (error) {
			showMessageOverlay({
				contextText: 'Failed to trigger print',
				info: error.message,
				error: true,
			});
		}
	};

	// Dimensions from ViewDocModal for consistency
	const borderRadius = scaleValue(12);
	const paddingY = scaleValue(48);
	const paddingX = scaleValue(24);
	const headerPaddingX = scaleValue(64);
	const modalWidth = '90%';
	const modalHeight = '85vh';
	const closeScale = scaleValue(2.0);

	return (
		<Modal
			open={true}
			onClose={handleClose}
			sx={{
				background: isDark ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.6)',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				overflow: 'auto',
			}}
		>
			<Box
				onClick={(e) => e.stopPropagation()}
				sx={{
					position: 'relative',
					background: isDark ? '#151A1E' : '#f2f2f2',
					borderRadius,
					width: modalWidth,
					height: modalHeight,
					overflow: 'hidden',
					zIndex: 1,
					display: 'flex',
					flexDirection: 'column',
					padding: `${paddingY}px ${paddingX}px`,
					color: isDark ? '#e6e6e6' : '#202020',
				}}
			>
				{/* HEADER */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						width: '100%',
						marginBottom: scaleValue(16),
						padding: `0 ${headerPaddingX}px`,
					}}
				>
					<Typography sx={{ fontSize: '30px', fontWeight: 600 }}>
						Reprint Barcode
					</Typography>

					<IconButton
						onClick={handleClose}
						sx={{
							transform: `scale(${closeScale})`,
							color: isDark ? '#8a8a8a' : '#acacac',
						}}
					>
						<CloseIcon fontSize="large" />
					</IconButton>
				</Box>

				{/* BLUE AREA (Like View Document) */}
				<Box
					sx={{
						backgroundColor: '#ECF9FF',
						width: '96%',
						height: '100%',
						margin: '0 auto',
						borderRadius: '12px',
						display: 'flex',
						flexDirection: 'column',
						padding: '40px',
						alignItems: 'center',
						justifyContent: 'center',
						gap: '30px',
						overflowY: 'auto'
					}}
				>
					<Box sx={{ width: '100%', maxWidth: '800px' }}>
						<Typography
							sx={{
								fontSize: '28px',
								fontWeight: 700,
								color: '#0F107B',
								mb: 4,
								textAlign: 'center',
							}}
						>
							Scan Barcode to Reprint
						</Typography>

						<ScanOrEnterBarcodeForm
							placeholder="SCAN BARCODE"
							onSubmit={onSubmit}
							enableAutoEnter
							resetTrigger={resetTrigger}
							textInputSx={{
								background: isDark ? '#1E1E1E' : '#fff',
								borderRadius: '12px',
								'& .MuiInputBase-input': {
									fontSize: '24px',
									padding: '16px 24px',
									color: isDark ? '#fff' : '#0F107B',
								},
								'& .MuiInputBase-input::placeholder': {
									color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(15, 16, 123, 0.5)',
								},
							}}
						/>
					</Box>

					<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
						<Typography sx={{ color: '#0F107B', opacity: 0.6, fontSize: '20px' }}>
							Scanning will immediately trigger printing
						</Typography>
						<Typography sx={{ color: '#0F107B', opacity: 0.5, fontSize: '16px', mt: 2 }}>
							Note: Reprint barcode is only available for Booked, Taken out and In flight parts
						</Typography>
					</Box>
				</Box>

				{/* FOOTER / BACK BUTTON */}
				<Box
					sx={{
						width: '100%',
						display: 'flex',
						justifyContent: 'flex-start',
						padding: `0 ${scaleValue(32)}px`,
						marginTop: '16px',
					}}
				>
					<Button
						onClick={handleClose}
						sx={{
							backgroundColor: '#0F107B',
							color: '#fff',
							fontSize: '18px',
							fontWeight: 600,
							padding: '10px 32px',
							borderRadius: '8px',
							textTransform: 'none',
							'&:hover': { backgroundColor: '#0A0A5A' },
						}}
					>
						Close
					</Button>
				</Box>
			</Box>
		</Modal>
	);
};

export default ReprintBarcodeModal;
